import React, { useRef, useState, useEffect } from 'react';
import { reactDnD, Icon } from '@yola/ws-ui';
import { assets } from '@yola/ws-sdk';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import GalleryImagePreview from './gallery-image-preview';
import GalleryIframePreview from './gallery-iframe-preview';
import constants from '../constants';
import mediaTypes from '../constants/media-types';
import getDropPosition from '../helpers/get-drop-position';
import getDragPreviewItemStackOrder from '../helpers/get-drag-preview-item-stack-order';
import isBlob from '../verifiers/is-blob';

const { useDrag, useDrop } = reactDnD;

const galleryItemImageClassName = 'ws-gallery-item__image';

function getPreviewSource(src, mediaType) {
  if (mediaType === mediaTypes.IFRAME) return src;
  return isBlob(src) ? src : assets.helpers.addBaseHref(src);
}

function GalleryItem({
  id,
  src,
  order,
  isPlaceholder = false,
  onItemClick = undefined,
  enableSelect,
  mediaType = mediaTypes.IMAGE,
  placeholderType = undefined,
  selectedItemsIds,
  items: allItems,
  onEndItemDrag,
}) {
  const [dropPosition, setDropPosition] = useState(null);
  const [isOnClickAllowed, setIsOnClickAllowed] = useState(true);
  const rootElement = useRef(null);

  const isSelected = selectedItemsIds.includes(id);

  const [{ isDragging, draggedItemsData }, drag] = useDrag(
    () => ({
      type: constants.dnd.DRAG_ITEM,
      item: () => {
        const imageElement = rootElement.current?.querySelector(`.${galleryItemImageClassName}`);
        const width = imageElement?.getBoundingClientRect().width || 0;

        document.body.classList.add(constants.dnd.DRAGGING_CLASS);

        let draggedItems;
        if (isSelected) {
          const selectedItems = allItems.filter((item) => selectedItemsIds.includes(item.id));

          draggedItems = selectedItems.map((item) => ({
            id: item.id,
            src: item.src,
            width,
            order: item.id === id ? order : null,
            isPlaceholder: item.isPlaceholder,
            previewStackOrder: getDragPreviewItemStackOrder({
              currentItemId: item.id,
              draggedItemId: id,
              draggedItems: selectedItems,
            }),
          }));
        } else {
          draggedItems = [{ id, src, order, width, isPlaceholder }];
        }

        return { draggedItemsData: draggedItems };
      },
      end: (item, monitor) => {
        const dropResult = monitor.getDropResult();

        if (dropResult) {
          const { draggedItemsData: items, dropTargetId, dropPosition: position } = dropResult;
          const draggedItemsIds = items.map((data) => data.id);

          onEndItemDrag({ draggedItemsIds, dropTargetId, dropPosition: position });
        }

        document.body.classList.remove(constants.dnd.DRAGGING_CLASS);
      },
      isDragging: () => true,
      collect: (monitor) => {
        const draggingData = monitor.getItem();
        return {
          isDragging: monitor.isDragging(),
          draggedItemsData: draggingData?.draggedItemsData,
        };
      },
    }),
    [id, src, order, isPlaceholder, isSelected, selectedItemsIds, allItems, onEndItemDrag]
  );

  // Drop target
  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: constants.dnd.DRAG_ITEM,
      hover: (item, monitor) => {
        if (!monitor.isOver({ shallow: true }) || !monitor.canDrop()) {
          setDropPosition(null);
          return;
        }

        const newDropPosition = getDropPosition(monitor, { rootElement });
        if (newDropPosition !== dropPosition) {
          setDropPosition(newDropPosition);
        }
      },
      canDrop: (item, monitor) => {
        if (!monitor.isOver({ shallow: true })) {
          return false;
        }

        const { draggedItemsData: items } = item;
        if (items.some((draggedItem) => draggedItem.id === id)) {
          return false;
        }

        return true;
      },
      drop: (item, monitor) => {
        if (!monitor.isOver({ shallow: true })) {
          return undefined;
        }

        const { draggedItemsData: items } = item;
        const position = getDropPosition(monitor, { rootElement });

        return {
          draggedItemsData: items,
          dropTargetId: id,
          dropPosition: position,
        };
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
      }),
    }),
    [id, dropPosition]
  );

  // Combine drag and drop refs
  const combinedRef = (node) => {
    rootElement.current = node;
    drag(drop(node));
  };

  // Manage if click on item is processed.
  // Needed to prevent spare click/mouseout
  // event trigger immediately after drag end.
  useEffect(() => {
    if (isDragging) {
      setIsOnClickAllowed(false);
    } else {
      const timer = setTimeout(() => {
        setIsOnClickAllowed(true);
      }, 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isDragging]);

  const isCurrentItemDragged = draggedItemsData && draggedItemsData.some((item) => item.id === id);
  const galleryItemClassName = classNames('ws-gallery-item', {
    'ws-gallery-item--active': isSelected,
  });
  const dropOutlineClassName = classNames('ws-gallery-item__drop-outline', {
    [`ws-gallery-item__drop-outline-${dropPosition}`]: isOver,
  });
  const previewSource = getPreviewSource(src, mediaType);

  const handleClick = (e) => {
    if (isOnClickAllowed) {
      onItemClick(e, id, isSelected);
    }
  };

  return (
    <div className={galleryItemClassName} onClick={handleClick} ref={combinedRef}>
      <div
        className={galleryItemImageClassName}
        style={{
          opacity: isCurrentItemDragged ? 0 : 1,
        }}
      >
        <div className="ws-gallery-item__preview">
          {mediaType === mediaTypes.IFRAME ? (
            <GalleryIframePreview src={previewSource} />
          ) : (
            <GalleryImagePreview
              url={previewSource}
              placeholderType={placeholderType}
              isPlaceholder={isPlaceholder}
            />
          )}
        </div>

        {enableSelect && isSelected && (
          <div className="ws-gallery-item__circle-icon">
            <Icon glyph="check" size={12} strokeWidth={3} />
          </div>
        )}

        {isDragging && (
          <div className="ws-gallery-item__circle-icon ws-gallery-item__order">{order + 1}</div>
        )}
      </div>

      {isDragging && <span className={dropOutlineClassName} />}
    </div>
  );
}

GalleryItem.propTypes = {
  src: PropTypes.string.isRequired,
  selectedItemsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  enableSelect: PropTypes.bool.isRequired,
  onItemClick: PropTypes.func,
  id: PropTypes.string.isRequired,
  mediaType: PropTypes.string,
  placeholderType: PropTypes.string,
  isPlaceholder: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      isPlaceholder: PropTypes.bool,
      mediaType: PropTypes.string,
    })
  ).isRequired,
  order: PropTypes.number.isRequired,
  onEndItemDrag: PropTypes.func.isRequired,
};

export default GalleryItem;
